import React, { useEffect, useRef, useState, type FormEvent, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type ProfileFull, type ProfileInput } from '../api.js';
import { Icon } from '../components/Icon.js';
import { Switch } from '../components/ui.js';
import { TelegramBotsCard } from '../components/TelegramBotsCard.js';
import { Topbar, useSubnav } from './AdminLayout.js';
import { useAuth } from '../auth.js';
import { setActiveProfileId, useActiveProfileId } from '../active-profile.js';
import { isAdminLevel, isSuperadmin } from '../utils/roles.js';
import { isolate } from '../utils/bidi.js';

const SECRET_FIELDS: ReadonlyArray<keyof ProfileInput> = [
  'yookassaSecretKey',
  'allpayApiKey',
  'sendpulseClientSecret',
  'telegramBotToken',
  'shorturlApiKey',
  'notionToken',
  'walletwalletApiKey',
];

const empty: ProfileInput = {
  name: '',
  isActive: true,
  yookassaShopId: '',
  yookassaSecretKey: '',
  allpayLogin: '',
  allpayApiKey: '',
  allpayVatEnabled: false,
  sendpulseClientId: '',
  sendpulseClientSecret: '',
  sendpulseFromEmail: '',
  sendpulseFromName: '',
  telegramBotToken: '',
  telegramChatId: '',
  telegramThreadId: '',
  shorturlApiUrl: '',
  shorturlApiKey: '',
  notionToken: '',
  notionDatabaseId: '',
  notionClientsDatabaseId: '',
  walletwalletApiKey: '',
  walletEnabled: false,
};

/** Секции страницы — порядок совпадает с карточками. «Удаление» — только суперадмину. */
const SECTIONS: { id: string; label: string; superadminOnly?: boolean }[] = [
  { id: 'sec-profile', label: 'Название' },
  { id: 'sec-yookassa', label: 'YooKassa' },
  { id: 'sec-allpay', label: 'AllPay' },
  { id: 'sec-notion', label: 'Notion' },
  { id: 'sec-sendpulse', label: 'SendPulse' },
  { id: 'sec-telegram', label: 'Telegram (уведомления)' },
  { id: 'sec-shorturl', label: 'Short URL' },
  { id: 'sec-wallet', label: 'Wallet (билеты)' },
  { id: 'sec-bots', label: 'Telegram (выдача билетов)' },
  { id: 'sec-danger', label: 'Удаление', superadminOnly: true },
];

function isMasked(value: string | undefined): boolean {
  return typeof value === 'string' && value.startsWith('••••');
}

/**
 * Настройки активного профиля — того, что выбран в переключателе слева.
 * Создание профиля живёт в переключателе («+ Профиль»), удаление — последней
 * секцией здесь; и то и другое только суперадмину.
 */
export function ProfileEdit(): JSX.Element {
  const [id] = useActiveProfileId();
  const nav = useNavigate();
  const { user } = useAuth();
  const superadmin = isSuperadmin(user?.role);

  const [form, setForm] = useState<ProfileInput>(empty);
  const [touched, setTouched] = useState<Set<keyof ProfileInput>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Удаление — только суперадмину; сервер откажет, если есть продукты или боты.
  const onDelete = async (): Promise<void> => {
    if (!confirm(`Удалить профиль «${isolate(form.name)}»? Это нельзя отменить.`)) return;
    setDeleting(true);
    setErr(null);
    try {
      await api.deleteProfile(id!);
      window.dispatchEvent(new Event('profiles-changed'));
      // Переключатель сам выберет первый оставшийся профиль.
      setActiveProfileId(null);
      nav('/admin/products');
    } catch (e) {
      const d = (e as { details?: { error?: string; productsCount?: number; botsCount?: number } }).details;
      const code = d?.error;
      setErr(
        code === 'profile_has_products'
          ? `Нельзя удалить: в профиле ${d?.productsCount ?? ''} продуктов. Сначала удали или перенеси их.`
          : code === 'profile_has_bots'
            ? `Нельзя удалить: у профиля ${d?.botsCount ?? ''} ботов выдачи билетов. Сначала удали их в секции «Боты билетов».`
            : `Не удалось удалить: ${code ?? (e as Error).message}`,
      );
    } finally {
      setDeleting(false);
    }
  };
  const [notionDbs, setNotionDbs] = useState<{ id: string; name: string }[] | null>(null);
  const [notionDbsLoading, setNotionDbsLoading] = useState(false);
  const [notionDbsErr, setNotionDbsErr] = useState<string | null>(null);
  // Навигация по секциям слева: подсвечивается та, что сейчас в видимой части.
  const [activeSection, setActiveSection] = useState<string>(SECTIONS[0]!.id);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const root = scrollRef.current;
    if (loading || !root) return;
    // Скроллится область страницы (как на всех страницах админки), меню при
    // этом стоит. Активна секция, чей верх последним прошёл над верхней кромкой
    // области; у самого низа — последняя, иначе до неё никогда не дойти, если
    // она короче экрана.
    const ids = SECTIONS.filter((sec) => !sec.superadminOnly || superadmin).map((sec) => sec.id);
    const pick = (): void => {
      // Страница не прокручена — активна первая секция. Без этого на высоком
      // экране верх второй секции уже стоял бы выше линии, и подсвечивалась
      // бы она, а не та, с которой страница начинается.
      if (root.scrollTop < 8) {
        setActiveSection(ids[0]!);
        return;
      }
      const atBottom = root.scrollTop + root.clientHeight >= root.scrollHeight - 2;
      if (atBottom) {
        setActiveSection(ids[ids.length - 1]!);
        return;
      }
      // Активна та секция, чей верх уже в верхней половине области: так
      // подсветка совпадает с тем, что человек видит, а не с тем, что только
      // что уехало вверх.
      const rootTop = root.getBoundingClientRect().top;
      const line = root.clientHeight * 0.45;
      let current = ids[0]!;
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top - rootTop <= line) current = id;
      }
      setActiveSection(current);
    };
    pick();
    // После клика по пункту страница скроллится сама и может упереться в низ;
    // тогда правило «у низа — последняя» перебило бы выбор. Поэтому во время
    // программной прокрутки подсветку не трогаем, а снова считаем её только
    // когда скроллит сам пользователь (колесо или клавиши).
    const onScroll = (): void => {
      if (pinnedUntil.current > Date.now()) return;
      pick();
    };
    const unpin = (): void => {
      pinnedUntil.current = 0;
    };
    root.addEventListener('scroll', onScroll, { passive: true });
    root.addEventListener('wheel', unpin, { passive: true });
    root.addEventListener('keydown', unpin);
    root.addEventListener('touchmove', unpin, { passive: true });
    return () => {
      root.removeEventListener('scroll', onScroll);
      root.removeEventListener('wheel', unpin);
      root.removeEventListener('keydown', unpin);
      root.removeEventListener('touchmove', unpin);
    };
  }, [loading, superadmin]);

  const pinnedUntil = useRef(0);
  const jump = (secId: string): void => {
    // Плавная прокрутка длится до секунды; на это время выбор закреплён.
    pinnedUntil.current = Date.now() + 1200;
    setActiveSection(secId);
    document.getElementById(secId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Секции страницы — подпунктами под «Настройки» в главном меню.
  useSubnav(
    loading
      ? null
      : {
          under: 'profiles',
          items: SECTIONS.filter((sec) => !sec.superadminOnly || superadmin).map(({ id, label }) => ({ id, label })),
          activeId: activeSection,
          onSelect: jump,
        },
  );

  useEffect(() => {
    if (user && !isAdminLevel(user.role)) {
      nav('/admin/products', { replace: true });
      return;
    }
    if (!id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr(null);
    setTouched(new Set());
    api
      .getProfile(id)
      .then((p: ProfileFull) => {
        setForm({
          name: p.name,
          isActive: p.isActive,
          yookassaShopId: p.yookassaShopId,
          yookassaSecretKey: p.yookassaSecretKey,
          allpayLogin: p.allpayLogin,
          allpayApiKey: p.allpayApiKey,
          allpayVatEnabled: p.allpayVatEnabled,
          sendpulseClientId: p.sendpulseClientId,
          sendpulseClientSecret: p.sendpulseClientSecret,
          sendpulseFromEmail: p.sendpulseFromEmail,
          sendpulseFromName: p.sendpulseFromName,
          telegramBotToken: p.telegramBotToken,
          telegramChatId: p.telegramChatId,
          telegramThreadId: p.telegramThreadId ?? '',
          shorturlApiUrl: p.shorturlApiUrl,
          shorturlApiKey: p.shorturlApiKey,
          notionToken: p.notionToken,
          notionDatabaseId: p.notionDatabaseId,
          notionClientsDatabaseId: p.notionClientsDatabaseId,
          walletwalletApiKey: p.walletwalletApiKey ?? '',
          walletEnabled: p.walletEnabled ?? false,
        });
        setLoading(false);
        // Auto-load Notion databases if token is configured
        if (p.notionToken) {
          api
            .listNotionDatabases(id!)
            .then((list) => setNotionDbs(list))
            .catch((e: Error) => setNotionDbsErr(e.message));
        }
      })
      .catch((e: Error) => {
        setErr(e.message);
        setLoading(false);
      });
  }, [id, user, nav]);

  const update = <K extends keyof ProfileInput>(k: K, v: ProfileInput[K]): void => {
    setForm((s) => ({ ...s, [k]: v }));
    setTouched((s) => new Set(s).add(k));
  };

  const loadNotionDbs = (): void => {
    const token = form.notionToken ?? '';
    if (!token) {
      setNotionDbsErr('Сначала введи Notion Token');
      return;
    }
    setNotionDbsLoading(true);
    setNotionDbsErr(null);
    const call = isMasked(token) ? api.listNotionDatabases(id!) : api.listNotionDatabasesByToken(token);
    call
      .then((list) => setNotionDbs(list))
      .catch((e: Error) => setNotionDbsErr(e.message))
      .finally(() => setNotionDbsLoading(false));
  };

  // Копия сохранённого профиля со всеми настройками, включая секреты (их
  // копирует сервер). Несохранённые правки в копию не попали бы — поэтому
  // кнопка доступна только без них.
  const onCopy = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      const copy = await api.copyProfile(id!);
      window.dispatchEvent(new Event('profiles-changed'));
      // Копия становится активным профилем — страница перечитает её сама.
      setActiveProfileId(copy.id);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      // For secrets, only send the value if user actually edited it AND it's not still a mask.
      const payload: Partial<ProfileInput> = { ...form };
      for (const f of SECRET_FIELDS) {
        const v = form[f] as string | undefined;
        if (!touched.has(f) || isMasked(v)) {
          delete payload[f];
        }
      }
      await api.updateProfile(id!, payload);
      window.dispatchEvent(new Event('profiles-changed'));
      setTouched(new Set());
      setSavedAt(Date.now());
    } catch (e) {
      const d = (e as { details?: unknown }).details;
      setErr(typeof d === 'string' ? d : JSON.stringify(d));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 32, color: 'var(--fg-subtle)' }}>Загрузка…</div>;
  }
  if (!id) {
    return (
      <>
        <Topbar title="Настройки" />
        <div style={{ padding: 32, color: 'var(--fg-subtle)', fontSize: 14 }}>
          {superadmin
            ? 'Профилей пока нет. Создай первый через «+ Профиль» в переключателе профиля слева.'
            : 'Тебе ещё не выдали ни одного профиля — попроси суперадмина.'}
        </div>
      </>
    );
  }

  return (
    <>
      <Topbar
        title={<>Настройки: <bdi>{form.name}</bdi></>}
        actions={
          <>
            {savedAt && touched.size === 0 && (
              <span style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>Сохранено</span>
            )}
            {superadmin && (
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => void onCopy()}
                disabled={busy || touched.size > 0}
                title={
                  touched.size > 0
                    ? 'Сначала сохраните изменения: копируется сохранённый профиль'
                    : 'Новый профиль с теми же настройками, ключами и флоу'
                }
              >
                Копировать
              </button>
            )}
            <button
              type="submit"
              form="profile-form"
              className="btn btn-primary btn-sm"
              disabled={busy || !form.name.trim()}
            >
              Сохранить
            </button>
          </>
        }
      />
      <div ref={scrollRef} style={{ padding: '20px 28px', flex: 1, overflowY: 'auto' }}>
        <div className="settings-main compact">
        <form id="profile-form" onSubmit={onSubmit} style={{ display: 'grid', gap: 12 }}>
          {err && (
            <div
              className="card"
              style={{
                padding: 14,
                color: 'var(--danger-fg)',
                background: 'var(--danger-subtle-bg)',
              }}
            >
              {err}
            </div>
          )}

          <Card id="sec-profile" title="Название">
            <Field label="Название *">
              <input
                className="input"
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                placeholder="Default"
              />
            </Field>
            <Row label="Активен">
              <Switch checked={form.isActive ?? true} onChange={(v) => update('isActive', v)} />
            </Row>
          </Card>

          <Card id="sec-yookassa" title="YooKassa (RUB)">
            <div className="grid-2">
              <Field label="Shop ID">
                <input
                  className="input mono"
                  value={form.yookassaShopId ?? ''}
                  onChange={(e) => update('yookassaShopId', e.target.value)}
                />
              </Field>
              <SecretField
                label="Secret key"
                value={form.yookassaSecretKey ?? ''}
                onChange={(v) => update('yookassaSecretKey', v)}
              />
            </div>
          </Card>

          <Card id="sec-allpay" title="AllPay (ILS)">
            <div className="grid-2">
              <Field label="Login">
                <input
                  className="input mono"
                  value={form.allpayLogin ?? ''}
                  onChange={(e) => update('allpayLogin', e.target.value)}
                />
              </Field>
              <SecretField
                label="API key"
                value={form.allpayApiKey ?? ''}
                onChange={(v) => update('allpayApiKey', v)}
              />
            </div>
            <Row label="Включить НДС">
              <Switch
                checked={form.allpayVatEnabled ?? false}
                onChange={(v) => update('allpayVatEnabled', v)}
              />
            </Row>
          </Card>

          <Card id="sec-notion" title="Notion" action={false}>
            <SecretField
              label="Token"
              value={form.notionToken ?? ''}
              onChange={(v) => update('notionToken', v)}
            />
            <Field label="База заказов">
              <NotionDbSelect
                dbs={notionDbs}
                value={form.notionDatabaseId ?? ''}
                onChange={(v) => update('notionDatabaseId', v)}
                placeholder="База с карточками заказов"
              />
            </Field>
            <Field label="База клиентов">
              <NotionDbSelect
                dbs={notionDbs}
                value={form.notionClientsDatabaseId ?? ''}
                onChange={(v) => update('notionClientsDatabaseId', v)}
                placeholder="База CRM-клиентов (опционально)"
              />
            </Field>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, paddingTop: 4 }}>
              {notionDbsErr && (
                <div style={{ fontSize: 12, color: 'var(--danger-fg)', flex: 1 }}>
                  {notionDbsErr}
                </div>
              )}
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={loadNotionDbs}
                disabled={notionDbsLoading}
                style={{ minWidth: 140 }}
              >
                {notionDbsLoading ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Spinner /> Загружаю…
                  </span>
                ) : notionDbs ? (
                  '↻ Обновить список'
                ) : (
                  '↻ Загрузить базы'
                )}
              </button>
            </div>
          </Card>

          <Card id="sec-sendpulse" title="SendPulse (письма)">
            <div className="grid-2">
              <Field label="Client ID">
                <input
                  className="input mono"
                  value={form.sendpulseClientId ?? ''}
                  onChange={(e) => update('sendpulseClientId', e.target.value)}
                />
              </Field>
              <SecretField
                label="Client secret"
                value={form.sendpulseClientSecret ?? ''}
                onChange={(v) => update('sendpulseClientSecret', v)}
              />
            </div>
            <div className="grid-2">
              <Field label="From email">
                <input
                  className="input"
                  type="email"
                  value={form.sendpulseFromEmail ?? ''}
                  onChange={(e) => update('sendpulseFromEmail', e.target.value)}
                />
              </Field>
              <Field label="From name">
                <input
                  className="input"
                  value={form.sendpulseFromName ?? ''}
                  onChange={(e) => update('sendpulseFromName', e.target.value)}
                />
              </Field>
            </div>
          </Card>

          <Card id="sec-telegram" title="Telegram (уведомления)">
            <div className="grid-2">
              <SecretField
                label="Bot token"
                value={form.telegramBotToken ?? ''}
                onChange={(v) => update('telegramBotToken', v)}
              />
              <Field label="Chat ID">
                <input
                  className="input mono"
                  value={form.telegramChatId ?? ''}
                  onChange={(e) => update('telegramChatId', e.target.value)}
                />
              </Field>
              <Field label="Топик (если в группе включены топики)">
                <input
                  className="input mono"
                  value={form.telegramThreadId ?? ''}
                  onChange={(e) => update('telegramThreadId', e.target.value)}
                  placeholder="9 или ссылка t.me/c/…/9"
                />
              </Field>
            </div>
          </Card>

          <Card id="sec-shorturl" title="Short URL (dgtl.am)">
            <Field label="API URL">
              <input
                className="input mono"
                value={form.shorturlApiUrl ?? ''}
                onChange={(e) => update('shorturlApiUrl', e.target.value)}
                placeholder="https://click.it.com"
              />
            </Field>
            <SecretField
              label="API key"
              value={form.shorturlApiKey ?? ''}
              onChange={(v) => update('shorturlApiKey', v)}
            />
          </Card>

          <Card id="sec-wallet" title="Wallet (WalletWallet)">
            <div style={{ fontSize: 12.5, color: 'var(--fg-subtle)' }}>
              Карточка билета сразу для Apple Wallet и Google Wallet. Выпускается автоматически после оплаты у
              продуктов с билетами и заполненным «Событием билета»; кнопки «Добавить в Wallet» — в письме, на
              странице после оплаты и в боте. Ключ — в кабинете walletwallet.dev.
            </div>
            <SecretField
              label="API key"
              value={form.walletwalletApiKey ?? ''}
              onChange={(v) => update('walletwalletApiKey', v)}
            />
            <Row label="Выпускать Wallet-билеты">
              <Switch
                checked={form.walletEnabled ?? false}
                onChange={(v) => update('walletEnabled', v)}
              />
            </Row>
            {form.walletEnabled && !form.walletwalletApiKey && (
              <div style={{ fontSize: 12, color: 'var(--danger-fg)' }}>
                Без ключа карточки не выпускаются — вставьте API key.
              </div>
            )}
          </Card>
        </form>
        {/* Боты — свой список со своими кнопками, поэтому вне формы профиля и
            только у сохранённого профиля: новому id ещё не выдан. */}
        <div id="sec-bots">
          <TelegramBotsCard profileId={id!} />
        </div>
        {superadmin && (
          <div className="card" id="sec-danger">
            <div className="card-header">
              <div className="card-title">Удаление профиля</div>
            </div>
            <div className="card-body stack">
              <div style={{ fontSize: 12.5, color: 'var(--fg-subtle)' }}>
                Профиль с продуктами или ботами выдачи билетов удалить нельзя — сначала убери их.
                Промокоды профиля удалятся вместе с ним. Доступы пользователей к этому профилю
                снимутся.
              </div>
              <div>
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  disabled={deleting || busy}
                  onClick={() => void onDelete()}
                >
                  <Icon name="trash" size={13} />
                  {deleting ? 'Удаляем…' : 'Удалить профиль'}
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </>
  );
}

function Card({ id, title, action, children }: { id?: string; title: string; action?: JSX.Element | false; children: React.ReactNode }): JSX.Element {
  return (
    <div className="card" id={id}>
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="card-title">{title}</div>
        {action}
      </div>
      <div className="card-body stack">{children}</div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: JSX.Element;
}): JSX.Element {
  return (
    <div className="field">
      <label className="field-label">{label}</label>
      {children}
      {hint ? <div className="field-hint">{hint}</div> : null}
    </div>
  );
}

function Row({ label, children }: { label: string; children: JSX.Element }): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
      {children}
    </div>
  );
}

const spinnerStyle = `@keyframes spin { to { transform: rotate(360deg); } }`;

function Spinner(): JSX.Element {
  return (
    <>
      <style>{spinnerStyle}</style>
      <span
        style={{
          display: 'inline-block',
          width: 12,
          height: 12,
          border: '2px solid currentColor',
          borderTopColor: 'transparent',
          borderRadius: '50%',
          animation: 'spin 0.7s linear infinite',
        }}
      />
    </>
  );
}

function NotionDbSelect({
  dbs,
  value,
  onChange,
  placeholder,
}: {
  dbs: { id: string; name: string }[] | null;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}): JSX.Element {
  const unknownValue = value && dbs && !dbs.some((d) => d.id === value);
  if (dbs) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <select
          className="input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">— не выбрано —</option>
          {unknownValue && <option value={value}>{value} (текущий)</option>}
          {dbs.map((d) => (
            <option key={d.id} value={d.id} dir="auto">{d.name}</option>
          ))}
        </select>
        {value && (
          <div style={{ fontSize: 11, color: 'var(--fg-subtle)', fontFamily: 'monospace' }}>
            {value}
          </div>
        )}
      </div>
    );
  }
  return (
    <input
      className="input mono"
      disabled
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder ?? 'Нажми «Загрузить базы» ниже'}
      style={{ opacity: 0.45, cursor: 'not-allowed' }}
    />
  );
}

function SecretField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  const [show, setShow] = useState(false);
  return (
    <div className="field">
      <label className="field-label">{label}</label>
      <div style={{ position: 'relative' }}>
        <input
          className="input mono"
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="new-password"
          style={{ paddingRight: 36 }}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            height: '100%',
            padding: '0 10px',
            background: 'transparent',
            border: 'none',
            color: 'var(--fg-subtle)',
            cursor: 'pointer',
            fontSize: 11,
          }}
        >
          {show ? 'скрыть' : 'показать'}
        </button>
      </div>
    </div>
  );
}
